import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { PagesComponent } from './pages.component';
import { NotFoundComponent } from '../not-found/not-found.component';

const routes: Routes = [
  {
    path: '',
    component: PagesComponent,
    children: [
      // Redirect the empty path straight to 'dashboard' instead of also
      // loading DashboardModule here. Previously BOTH '' and 'dashboard'
      // mounted DashboardComponent and PagesComponent.ngOnInit navigated
      // '' -> 'dashboard', so the dashboard mounted twice and every dashboard
      // API call (Transactions, bot/instance status, Utilization, search,
      // errors) fired twice. A single redirect mounts it exactly once.
      {
        path: '',
        redirectTo: 'newdashboard',
        pathMatch: 'full'
      },
      // The OLD dashboard. No longer listed in the sidebar and no longer the
      // landing page, but deliberately still routed: bookmarks and any link that
      // survives out there keep working, and it remains a fallback while the
      // revamped one beds in. Delete this route (and pages/dashboard/) once there
      // is no reason to go back to it.
      {
        path: 'dashboard',
        loadChildren: () => import('./dashboard/dashboard.module').then((x) => x.DashboardModule)
      },
      // The revamped dashboard (Requirements/REVAMP_Dashboard) — now the default
      // landing page and the only one in the sidebar.
      {
        path: 'newdashboard',
        loadChildren: () => import('./newdashboard/newdashboard.module').then((x) => x.NewDashboardModule)
      },
      {
        path: 'instances',
        loadChildren: () => import('./instances/instances.module').then((x) => x.InstancesModule)
      },
      {
        path: 'triggers',
        loadChildren: () => import('./triggers/triggers.module').then((x) => x.TriggersModule)
      },
      {
        path: 'bots',
        loadChildren: () => import('./bots/bots.module').then((x) => x.BotsModule)
      },
      // The legacy Flows page is gone; a bookmarked URL lands on the Flows page.
      {
        path: 'flows',
        redirectTo: 'new-flows',
        pathMatch: 'full'
      },
      {
        path: 'new-flows',
        loadChildren: () => import('./new-flows/new-flows.module').then((x) => x.NewFlowsModule)
      },
      {
        path: 'jobs',
        loadChildren: () => import('./jobs/jobs.module').then((x) => x.JobsModule)
      },
      {
        path: 'libraries',
        loadChildren: () => import('./libraries/libraries.module').then((x) => x.LibrariesModule)
      },
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then((x) => x.SettingsModule)
      },
      {
        path: 'master',
        loadChildren: () => import('./master/master.module').then((x) => x.MasterModule)
      },
      {
        path: 'logs',
        loadChildren: () => import('./logs/logs.module').then((x) => x.LogsModule)
      },
      {
        path: 'configuration',
        loadChildren: () => import('./configuration/configuration.module').then((x) => x.ConfigurationModule)
      },
      {
        path: 'apikeys',
        loadChildren: () => import('./apikeys/apikeys.module').then((x) => x.ApikeysModule)
      },
      {
        path: 'credentialstore',
        loadChildren: () => import('./credentialstore/credentialstore.module').then((x) => x.CredentialstoreModule)
      },
      {
        path: 'queues',
        loadChildren: () => import('./queues/queue/queue.module').then((x) => x.QueueModule)
      },
      {
        path: 'itempriority',
        loadChildren: () => import('./queues/item-priority/item-priority.module').then((x) => x.ItemPriorityModule)
      },
      {
        path: 'customstatus',
        loadChildren: () => import('./queues/custom-status/custom-status.module').then((x) => x.CustomStatusModule)
      },
      {
        path: 'notifications',
        loadChildren: () => import('./notificattions/notificattions.module').then((x) => x.NotificattionsModule)
      },
      {
        path: 'tenants',
        loadChildren: () => import('./tenantlist/tenantlist.module').then((x) => x.TenantlistModule)
      },
      {
        path: 'apptokens',
        loadChildren: () => import('./apptokens/apptokens.module').then((x) => x.ApptokensModule)
      },
      {
        path: 'formbuilder',
        loadChildren: () => import('./formbuilder/formbuilder.module').then((x) => x.FormbuilderModule)
      },
      {
        path: 'taskcenter',
        loadChildren: () => import('./taskcenter/taskcenter.module').then((x) => x.TaskcenterModule)
      },
      {
        path: 'validationtask',
        loadChildren: () => import('./validationtask/validationtask.module').then((x) => x.ValidationtaskModule)
      },
      {
        path: 'customermodel',
        loadChildren: () => import('./customermodel/customermodel.module').then((x) => x.CustomermodelModule)
      },
      {
        path: 'ocr',
        loadChildren: () => import('./ocr/ocr.module').then((x) => x.OcrModule)
      },
      {
        path: 'createtable',
        loadChildren: () => import('./createtables/createtables.module').then((x) => x.CreatetablesModule)
      },
      {
        path: 'knowledgebase',
        loadChildren: () => import('./knowledgebase/knowledgebase.module').then((x) => x.KnowledgebaseModule)
      },
      // KB Reports — Requirements/KBV2New/SPEC_Documents/KB_Reports_Implementation_Plan.md
      {
        path: 'reports',
        loadChildren: () => import('./reports/reports.module').then((x) => x.ReportsModule)
      },
      // Ontology — Requirements/KBIngestionImprovements/Ontology (Addendum 2 §6)
      {
        path: 'ontology',
        loadChildren: () => import('./ontology/ontology.module').then((x) => x.OntologyModule)
      },
      {
        path: 'consumtionhistory',
        loadChildren: () => import('./consumtionhistory/consumtionhistory.module').then((x) => x.ConsumtionhistoryModule)
      },
      {
        path: 'agent',
        loadChildren: () => import('./agent/agent.module').then((x) => x.AgentModule)
      },
      {
        // AI Agents V3 P6-WP9: the staff Chat page.
        path: 'chat',
        loadChildren: () => import('./chat/chat.module').then((x) => x.ChatModule)
      },
      {
        path: 'webflowrunner',
        loadChildren: () => import('./webflowrunner/webflowrunner.module').then((x) => x.WebflowrunnerModule)
      },
      {
        path: 'modelconfig',
        loadChildren: () => import('./modelconfig/modelconfig.module').then((x) => x.ModelconfigModule)
      },
      {
        path: 'enterprisesearch',
        loadChildren: () => import('./enterprisesearch/enterprisesearch.module').then((x) => x.EnterprisesearchModule)
      },
      {
        path: 'json-canvas-tester',
        loadChildren: () => import('./json-canvas-tester/json-canvas-tester.module').then((x) => x.JsonCanvasTesterModule)
      },
      {
        path: 'extractiq',
        loadChildren: () => import('./extractiq/extractiq.module').then((x) => x.ExtractIQModule)
      },
      // Any other address under the tenant: "Page not found" inside the app shell (sidebar,
      // topbar). Must stay LAST. Without it the top-level '**' catches the URL and renders
      // outside the shell.
      {
        path: '**',
        component: NotFoundComponent
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class PagesRoutingModule { }

